/**
 * Experience an hour, measured over the last fifteen minutes, and how long
 * the next level is at that pace (2026-10-21: "add an "XP rate" so players
 * can get an idea of how effective their current hunting strategy is. it
 * should probably estimate time to next level").
 *
 * Fifteen minutes because it is a hunt rather than a session: long enough
 * that one fat kill does not read as a fortune, short enough that moving to
 * a better spot shows in the number before the trip is over, and walking
 * back to town runs it down the way it should. It is counted on the wall
 * clock, so standing about is part of what a strategy costs.
 *
 * It lives in the page, not the server: it starts over when the page is
 * loaded or a different character logs in, and on any loss of experience,
 * a death, since a fall is not a rate anybody is hunting at. Until a minute
 * has been watched there is nothing to say, so it says nothing rather than
 * calling the first kill an hourly wage.
 *
 * The same watch is kept on every skill's uses, for the time to its next
 * level in its hover (2026-10-22: "show an estimate of how long to level a
 * skill too, in the tooltip").
 */
export const XP_RATE_WINDOW_MS = 15 * 60 * 1000;
const XP_RATE_MIN_MS = 60 * 1000;

/** A count watched over the window: experience, or a skill's uses. */
export class RateWatch {
  private who = "";
  private start = 0;
  /** (time, experience) at each change, oldest first; the first is what
   *  the experience was at the start of the window, or of the watching. */
  private samples: { t: number; xp: number }[] = [];

  /** Takes the count as it is now, for the character called who. */
  note(who: string, xp: number, now: number) {
    const last = this.samples[this.samples.length - 1];
    if (who !== this.who || !last || xp < last.xp) {
      this.who = who;
      this.start = now;
      this.samples = [{ t: now, xp }];
      return;
    }
    if (xp !== last.xp) this.samples.push({ t: now, xp });
    // Keep one sample at or before the window's edge: it is the experience
    // the window starts from.
    const edge = now - XP_RATE_WINDOW_MS;
    while (this.samples.length > 1 && this.samples[1].t <= edge) this.samples.shift();
  }

  /** The count gained an hour, or undefined while too little has been watched. */
  perHour(now: number): number | undefined {
    const watched = Math.min(now - this.start, XP_RATE_WINDOW_MS);
    if (watched < XP_RATE_MIN_MS || this.samples.length === 0) return undefined;
    const edge = now - XP_RATE_WINDOW_MS;
    let from = this.samples[0].xp;
    for (const s of this.samples) {
      if (s.t > edge) break;
      from = s.xp;
    }
    const gained = this.samples[this.samples.length - 1].xp - from;
    return (gained * 3600 * 1000) / watched;
  }

  /** Minutes the window has watched, up to its fifteen. */
  watchedMins(now: number): number {
    return Math.floor(Math.min(now - this.start, XP_RATE_WINDOW_MS) / 60000);
  }
}

/** A time to wait, roughly: "under a minute", "40 min", "2 h 05 min",
 *  "over 100 h". */
export function roughWait(secs: number): string {
  if (secs < 60) return "under a minute";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  if (h >= 100) return "over 100 h";
  return `${h} h ${String(mins - h * 60).padStart(2, "0")} min`;
}
